package fr.manyverse.app.presentation.components.modules.whiteboard

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.GridOn
import androidx.compose.material.icons.filled.Minimize
import androidx.compose.material3.FilledIconButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButtonDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.unit.dp
import androidx.navigationevent.NavigationEventInfo
import androidx.navigationevent.compose.NavigationBackHandler
import androidx.navigationevent.compose.rememberNavigationEventState
import fr.manyverse.app.domain.model.DrawPoint
import fr.manyverse.app.domain.model.WhiteboardAction
import fr.manyverse.app.domain.model.WhiteboardColor
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.whiteboard_clear_all
import manyverse.shared.generated.resources.whiteboard_close_for_all
import manyverse.shared.generated.resources.whiteboard_minimize
import manyverse.shared.generated.resources.whiteboard_toggle_grid
import org.jetbrains.compose.resources.stringResource

/**
 * Fullscreen overlay for displaying the whiteboard.
 * GM version includes drawing capabilities and toolbar.
 */
@Composable
fun WhiteboardGmOverlay(
    isVisible: Boolean,
    isGM: Boolean,
    strokes: List<WhiteboardAction.Stroke>,
    showGrid: Boolean,
    selectedColor: WhiteboardColor,
    strokeWidth: Float,
    selectedTool: WhiteboardTool,
    onStrokeComplete: (List<DrawPoint>) -> Unit,
    onColorSelect: (WhiteboardColor) -> Unit,
    onToolSelect: (WhiteboardTool) -> Unit,
    onToggleGrid: () -> Unit,
    onClearAll: () -> Unit,
    onMinimize: () -> Unit,
    onCloseForAll: (() -> Unit)? = null,
    modifier: Modifier = Modifier
) {

    if (!isVisible) return

    NavigationBackHandler(
        state = rememberNavigationEventState(NavigationEventInfo.None),
        onBackCompleted = {}
    )

    // Le Dialog prenait le focus et fermait ainsi le clavier : sans ça, un clavier ouvert
    // (chat, notes) resterait par-dessus le tableau
    val focusManager = LocalFocusManager.current
    LaunchedEffect(Unit) { focusManager.clearFocus() }

    // Box plein écran dans l'arbre de l'écran, et non un Dialog : l'ordre d'affichage avec les
    // autres overlays (annonce, digicode…) suit ainsi l'ordre de déclaration dans l'écran, et on
    // évite la scène séparée du Dialog sur iOS.
    Box(
        modifier = modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.background)
            // Le nœud de saisie fait du tableau la cible du hit test : l'écran dessous ne reçoit
            // plus les touches, sans rien consommer (le canvas garde ses gestes)
            .pointerInput(Unit) {}
    ) {
        // Whiteboard canvas
        if (isGM) {
            WhiteboardCanvas(
                strokes = strokes,
                showGrid = showGrid,
                isDrawingEnabled = true,
                currentColor = selectedColor,
                currentStrokeWidth = strokeWidth,
                currentTool = selectedTool,
                onStrokeComplete = onStrokeComplete,
                modifier = Modifier.fillMaxSize()
            )
        } else {
            WhiteboardReadOnlyCanvas(
                strokes = strokes,
                showGrid = showGrid,
                modifier = Modifier.fillMaxSize()
            )
        }

        // Action buttons (top right corner)
        Row(
            modifier = Modifier
                .align(Alignment.TopEnd)
                .statusBarsPadding()
                .padding(16.dp)
        ) {
            // Board actions (GM only): grid then clear all
            if (isGM) {
                FilledIconButton(
                    onClick = onToggleGrid,
                    modifier = Modifier
                        .padding(end = 8.dp)
                        .size(48.dp),
                    shape = CircleShape,
                    colors = IconButtonDefaults.filledIconButtonColors(
                        containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.9f)
                    )
                ) {
                    Icon(
                        imageVector = Icons.Default.GridOn,
                        contentDescription = stringResource(Res.string.whiteboard_toggle_grid),
                        tint = if (showGrid) {
                            MaterialTheme.colorScheme.primary
                        } else {
                            MaterialTheme.colorScheme.onSurfaceVariant
                        }
                    )
                }

                // Destructive, but kept on a neutral container so it never reads
                // as the full-red "close for all" button
                FilledIconButton(
                    onClick = onClearAll,
                    modifier = Modifier
                        .padding(end = 8.dp)
                        .size(48.dp),
                    shape = CircleShape,
                    colors = IconButtonDefaults.filledIconButtonColors(
                        containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.9f)
                    )
                ) {
                    Icon(
                        imageVector = Icons.Default.Delete,
                        contentDescription = stringResource(Res.string.whiteboard_clear_all),
                        tint = MaterialTheme.colorScheme.error
                    )
                }
            }

            // Minimize button (available for everyone)
            FilledIconButton(
                onClick = onMinimize,
                modifier = Modifier
                    .size(48.dp),
                shape = CircleShape,
                colors = IconButtonDefaults.filledIconButtonColors(
                    containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.9f)
                )
            ) {
                Icon(
                    imageVector = Icons.Default.Minimize,
                    contentDescription = stringResource(Res.string.whiteboard_minimize),
                    tint = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }

            // Close for all button (GM only)
            if (onCloseForAll != null) {
                FilledIconButton(
                    onClick = onCloseForAll,
                    modifier = Modifier
                        .padding(start = 8.dp)
                        .size(48.dp),
                    shape = CircleShape,
                    colors = IconButtonDefaults.filledIconButtonColors(
                        containerColor = MaterialTheme.colorScheme.errorContainer.copy(alpha = 0.9f)
                    )
                ) {
                    Icon(
                        imageVector = Icons.Default.Close,
                        contentDescription = stringResource(Res.string.whiteboard_close_for_all),
                        tint = MaterialTheme.colorScheme.onErrorContainer
                    )
                }
            }
        }

        // GM toolbar (bottom center)
        if (isGM) {
            WhiteboardGmToolbar(
                selectedColor = selectedColor,
                selectedTool = selectedTool,
                onColorSelect = { color ->
                    onColorSelect(color)
                    // Picking a color always means drawing again
                    onToolSelect(WhiteboardTool.PEN)
                },
                onToolSelect = onToolSelect,
                modifier = Modifier
                    .align(Alignment.BottomCenter)
                    .navigationBarsPadding()
                    .padding(16.dp)
            )
        }
    }
}